import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ActionIcon, Button, Group, Input, ScrollArea, Stack, Tooltip } from "@mantine/core";
import { useChatHistoryOpen, useChatId } from "../../hooks/assistant.js";
import { useAppAction, useAppSelector } from "../../hooks/redux.js";
import { useCaptureEvent, useDebouncedCaptureEvent } from "../../components/StyleEditorContext.js";
import { useTRPC } from "../../trpc.js";
import { useMutation } from "@tanstack/react-query";
import { Event } from "../../constants/events.js";
import { useCallback, useState } from "react";
import { toggleHistory } from "../../reducers/chatReducer.js";
import { useChatListStore } from "../../hooks/useChatListStore.js";
import { useChatStore } from "../../hooks/useChatStore.js";
import { getInitStyle } from "../../reducers/initReducer.js";
import { useIntl } from "react-intl";
import { IconEdit, IconLayoutSidebarLeftCollapse, IconSearch, IconX } from "@tabler/icons-react";
import { useResizeObserver } from "@mantine/hooks";
import { ChatGroup } from "./ChatGroup.js";
import { RenameChatModal } from "./RenameChatModal.js";
import { sub, isWithinInterval } from "date-fns";
import { useChatChangeStore } from "../../hooks/useChatChangeStore.js";
const sortAndFilterChats = (chats, search) => {
    const searchLower = search.toLowerCase().trim();
    return chats.filter((chat) => {
        if (!chat.messages.length || !chat.summary)
            return false;
        if (!searchLower)
            return true;
        const summaryMatch = chat.summary.toLowerCase().includes(searchLower);
        if (summaryMatch)
            return true;
        const attachmentMatch = chat.messages.some((message) => {
            return "attachments" in message && message.attachments?.some((attachment) => {
                return attachment.name.toLowerCase().includes(searchLower);
            });
        });
        if (attachmentMatch)
            return true;
        const messageMatch = chat.messages.some((message) => {
            return message.content.some((block) => {
                return block.type === "text" && block.text.toLowerCase().includes(searchLower);
            });
        });
        if (messageMatch)
            return true;
        return false;
    }).toSorted((a, b) => {
        const lastMessageA = new Date(a.createdAt);
        const lastMessageB = new Date(b.createdAt);
        return lastMessageB.valueOf() - lastMessageA.valueOf();
    });
};
const useGroupChats = (ungroupedChats) => {
    const intl = useIntl();
    const now = new Date();
    const twentyFourHoursAgo = sub(now, { days: 1 });
    const sevenDaysAgo = sub(now, { days: 7 });
    const thirtyDaysAgo = sub(now, { days: 30 });
    const starredChats = ungroupedChats.filter(chat => chat.starred);
    const unstarredChats = ungroupedChats.filter(chat => !chat.starred);
    const generateGroup = (name, start, end) => ({
        name,
        chats: unstarredChats.filter(chat => isWithinInterval(chat.lastMessageAt, { start, end })),
    });
    const groups = [
        generateGroup(intl.formatMessage({ id: "groupedChats.today", defaultMessage: "Today" }), now, twentyFourHoursAgo),
        generateGroup(intl.formatMessage({ id: "groupedChats.week", defaultMessage: "Last 7 days" }), twentyFourHoursAgo, sevenDaysAgo),
        generateGroup(intl.formatMessage({ id: "groupedChats.month", defaultMessage: "Last 30 days" }), sevenDaysAgo, thirtyDaysAgo),
        generateGroup(intl.formatMessage({ id: "groupedChats.older", defaultMessage: "Older" }), thirtyDaysAgo, new Date(0)),
    ].filter(group => group.chats.length > 0);
    if (starredChats.length > 0) {
        groups.unshift({
            name: intl.formatMessage({ id: "groupedChats.starred", defaultMessage: "Starred" }),
            chats: starredChats,
        });
    }
    return groups;
};
export const ChatList = () => {
    const intl = useIntl();
    const trpc = useTRPC();
    const style = useAppSelector(getInitStyle);
    const chatId = useChatId();
    const toggle = useAppAction(toggleHistory);
    const { chats } = useChatListStore(style.id);
    const { changeChat, newChat } = useChatChangeStore(style.id);
    const { messages } = useChatStore(style.id, chatId);
    const captureEvent = useCaptureEvent();
    const debouncedCaptureEvent = useDebouncedCaptureEvent();
    const [searchQuery, setSearchQuery] = useState("");
    const { mutate: deleteChat } = useMutation(trpc.deleteChat.mutationOptions());
    const { mutate: starChat } = useMutation(trpc.starChat.mutationOptions());
    const hasMessages = messages.length > 0;
    const historyOpen = useChatHistoryOpen();
    const [ref, { width }] = useResizeObserver();
    const [renameModal, setRenameModal] = useState(null);
    const handleSwitchChat = useCallback((id) => {
        captureEvent(Event.ChangeChat);
        changeChat(id);
    }, [captureEvent, changeChat]);
    const handleDeleteChat = useCallback((id) => {
        captureEvent(Event.DeleteChat);
        if (id === chatId) {
            void newChat();
        }
        deleteChat(id);
    }, [chatId, deleteChat, newChat, captureEvent]);
    const handleRenameChat = useCallback((id, summary) => {
        captureEvent(Event.RenameChat);
        setRenameModal({ chatId: id, summary });
    }, [captureEvent]);
    const handleStarChat = useCallback((id, starred) => {
        captureEvent(Event.StarChat);
        starChat({ chatId: id, starred });
    }, [captureEvent, starChat]);
    const handleCloseRenameModal = useCallback(() => {
        setRenameModal(null);
    }, []);
    const handleToggleChatList = useCallback(() => {
        captureEvent(Event.ToggleChatHistory);
        toggle();
    }, [captureEvent, toggle]);
    const handleStartNewChat = useCallback(() => {
        if (!hasMessages)
            return;
        captureEvent(Event.CreateNewChat);
        void newChat();
    }, [hasMessages, intl, captureEvent, newChat]);
    const handleSearch = useCallback((event) => {
        debouncedCaptureEvent(Event.CreateNewChat);
        setSearchQuery(event.currentTarget.value);
    }, []);
    const clearSearch = useCallback(() => {
        captureEvent(Event.ClearChatSearch);
        setSearchQuery("");
    }, []);
    const clearSearchLabel = intl.formatMessage({
        defaultMessage: "Clear search",
        id: "label.clearSearch",
    });
    const searchPlaceholder = intl.formatMessage({
        defaultMessage: "Search your chats",
        id: "placeholder.searchChats",
    });
    const collapseLabel = intl.formatMessage({
        defaultMessage: "Collapse chat history",
        id: "button.collapseChatHistory",
    });
    const newChatLabel = intl.formatMessage({
        defaultMessage: "New chat",
        id: "label.newChat",
    });
    const sortedAndFilteredChats = sortAndFilterChats(chats, searchQuery);
    const groupedChats = useGroupChats(sortedAndFilteredChats);
    /**
     * We want to keep chat list processing even if the chat is closed
     */
    const display = historyOpen ? "flex" : "none";
    return (_jsxs(Stack, { h: "100%", w: "100%", flex: "0 0 280px", px: "sm", gap: "md", ref: ref, pt: "sm", display: display, style: {
            borderRight: "2px solid light-dark(black, white)",
        }, children: [_jsxs(Group, { gap: 8, w: "100%", wrap: "nowrap", children: [_jsx(Tooltip, { label: collapseLabel, children: _jsx(ActionIcon, { variant: "subtle", color: "black", onClick: handleToggleChatList, "aria-label": collapseLabel, children: _jsx(IconLayoutSidebarLeftCollapse, { size: 24 }) }) }), _jsx(Button, { flex: 1, variant: "light", color: "black", leftSection: _jsx(IconEdit, { size: 24 }), size: "sm", onClick: handleStartNewChat, children: newChatLabel })] }), _jsx(Group, { children: _jsx(Input, { variant: "transparent", value: searchQuery, bg: "light-dark(#EDEEEC, #54534B)", onChange: handleSearch, placeholder: searchPlaceholder, leftSection: _jsx(IconSearch, { size: 16 }), rightSectionPointerEvents: "all", rightSection: searchQuery && (_jsx(IconX, { size: 16, "aria-label": clearSearchLabel, onClick: clearSearch })) }) }), _jsx(ScrollArea, { children: groupedChats.map((group, index) => (_jsx(ChatGroup, { group: group, searchQuery: searchQuery, handleSwitchChat: handleSwitchChat, handleDeleteChat: handleDeleteChat, handleRenameChat: handleRenameChat, handleStarChat: handleStarChat, itemsWidth: width - 28, chatId: chatId }, index))) }), renameModal && (_jsx(RenameChatModal, { opened: true, onClose: handleCloseRenameModal, chatId: renameModal.chatId, currentSummary: renameModal.summary }))] }));
};
